import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Pencil, Share2 } from 'lucide-react';
import { errorText } from '../../lib/apiErrors';
import { parseInstant } from '../../lib/timezone';
import { teammateLabels } from '../../lib/teammateLabels';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const STATUS = {
    resolved: { label: 'Credited', tone: 'bg-emerald-100 text-emerald-700' },
    inactive: { label: 'Referrer inactive', tone: 'bg-amber-100 text-amber-700' },
    unresolved: { label: 'No teammate matched', tone: 'bg-stone-100 text-stone-600' },
    removed: { label: 'Credit removed', tone: 'bg-stone-100 text-stone-600' },
};

/** The one sentence for a contact whose Original Acquisition was never recorded. */
function NotRecorded({ className }) {
    return (
        <p className={className} data-testid="original-not-recorded">
            Original Acquisition was not recorded for this contact.
        </p>
    );
}

/** One credit's rows. `prefix` keeps today's `attribution-*` test ids on the current credit. */
function CreditRows({ credit, nameFor, prefix }) {
    const credited = credit.referred_by_user_id;
    const when = parseInstant(credit.attributed_at);
    return (
        <dl className="grid grid-cols-[88px_1fr] gap-x-2 gap-y-1.5 text-[13px] m-0">
            <dt className="text-stone-500">Referred by</dt>
            <dd className="m-0 text-stone-900" data-testid={`${prefix}-referrer`}>
                {credited ? (nameFor(credited) || 'A former teammate') : '—'}
                {credit.status !== 'resolved' && credit.referral_value && (
                    <span className="block text-xs text-stone-500">sent as <span className="font-mono">{credit.referral_value}</span></span>
                )}
            </dd>
            <dt className="text-stone-500">Source</dt>
            <dd className="m-0" data-testid={`${prefix}-source`}>{credit.source_name || '—'}</dd>
            {credit.campaign && (<><dt className="text-stone-500">Campaign</dt><dd className="m-0 font-mono text-xs">{credit.campaign}</dd></>)}
            {when && (<><dt className="text-stone-500">When</dt><dd className="m-0">{when.toLocaleDateString()}</dd></>)}
        </dl>
    );
}

/**
 * ContactDetail → Attribution (#793 S6; Original Acquisition, #864).
 *
 * - **Original Acquisition** — the permanent first-touch credit
 *   (`original_attribution`, written once, #861). A contact credited before
 *   #861 and never corrected has no snapshot: its effective credit IS the
 *   original (#860 C12 reader fallback).
 * - **Referral credit (current)** — shown only once the credit was corrected
 *   (`attribution.corrected_at`); never by deep-comparing the two.
 * A contact with no credit at all gets a minimal panel that says Original Acquisition was not recorded, so
 * the role is always labelled on Contact Detail (#889).
 *
 * Admins can correct the credit (`PUT /api/contacts/{id}/attribution`). That
 * moves the referral credit only — Original Acquisition and the Contact Owner
 * stay as they are — and leaves a change-log entry and a timeline row.
 */
export default function ContactAttributionPanel({ contact, teamMembers, isAdmin, getAuthHeader, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [referrer, setReferrer] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const attribution = contact?.attribution;
    const original = contact?.original_attribution;
    if (!attribution && !original) {
        return (
            <div className="p-4 border-b border-stone-100" data-testid="contact-attribution-panel">
                <div className="flex items-center gap-2 mb-3">
                    <Share2 className="w-3.5 h-3.5 text-stone-400" />
                    <h4 className="flex-1 text-xs font-semibold tracking-wider uppercase text-stone-400">Attribution</h4>
                </div>
                <NotRecorded className="text-[12px] text-stone-500" />
            </div>
        );
    }

    // #799 QA: a name two teammates share is shown with each one's email.
    const labels = teammateLabels(teamMembers);
    const nameFor = (userId) => labels.get(userId);
    const corrected = !!attribution?.corrected_at;
    // C12: no snapshot and never corrected → the effective credit is the original.
    const fallbackOriginal = !original && !!attribution && !corrected;
    const status = STATUS[(attribution || original).status] || STATUS.unresolved;
    const credited = attribution?.referred_by_user_id;
    const recovered = original?.captured_by === 'backfill'
        && ['reconstructed', 'one_level'].includes(original?.backfill_confidence);

    const startEdit = () => {
        setReferrer(credited || '');
        setNote('');
        setEditing(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(
                `${API}/contacts/${contact.contact_id}/attribution`,
                { referred_by_user_id: referrer || null, note: note.trim() || null },
                { headers: getAuthHeader() },
            );
            toast.success(referrer ? 'Referral credit updated' : 'Referral credit removed');
            setEditing(false);
            if (onChanged) onChanged();
        } catch (err) {
            // #861: the correction is a compare-and-swap on the credit as read.
            if (err?.response?.status === 409 && err?.response?.data?.detail?.code === 'ATTRIBUTION_CHANGED') {
                toast.error('Someone else just changed this credit — reload to review.');
                setEditing(false);
                if (onChanged) onChanged();
            } else {
                toast.error(errorText(err, 'Could not change the credit.'));
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4 border-b border-stone-100" data-testid="contact-attribution-panel">
            <div className="flex items-center gap-2 mb-3">
                <Share2 className="w-3.5 h-3.5 text-stone-400" />
                <h4 className="flex-1 text-xs font-semibold tracking-wider uppercase text-stone-400">Attribution</h4>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${status.tone}`} data-testid="attribution-status">{status.label}</span>
            </div>
            {!editing ? (
                <div className="space-y-3">
                    {(original || fallbackOriginal) && (
                        <section data-testid="original-acquisition">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <Lock className="w-3 h-3 text-stone-400" aria-hidden="true" />
                                <span className="text-[12px] font-semibold text-stone-700">Original Acquisition</span>
                                <span className="text-[11px] text-stone-400">Permanent first-touch credit</span>
                            </div>
                            <CreditRows credit={original || attribution} nameFor={nameFor}
                                        prefix={fallbackOriginal ? 'attribution' : 'original'} />
                            {recovered && (
                                <p className="mt-1 text-[11px] text-stone-400" data-testid="original-recovered">Recovered from correction history</p>
                            )}
                        </section>
                    )}
                    {attribution && (corrected || (!original && !fallbackOriginal)) && (
                        <section data-testid="current-referral-credit">
                            <div className="text-[12px] font-semibold text-stone-700 mb-1.5">Referral credit (current)</div>
                            <CreditRows credit={attribution} nameFor={nameFor} prefix="attribution" />
                            {corrected && (
                                <p className="mt-1 text-[12px] text-stone-500" data-testid="attribution-corrected-by">
                                    by {nameFor(attribution.corrected_by) || attribution.corrected_by_name || 'an admin'}
                                </p>
                            )}
                            {!original && (
                                <NotRecorded className="mt-1 text-[11px] text-stone-400" />
                            )}
                        </section>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    <select
                        value={referrer}
                        onChange={(e) => setReferrer(e.target.value)}
                        className="h-8 px-2 text-sm border border-stone-200 rounded-md bg-white"
                        data-testid="attribution-referrer-select"
                    >
                        <option value="">No one (remove the credit)</option>
                        {(teamMembers || []).map((m) => <option key={m.user_id} value={m.user_id}>{labels.get(m.user_id)}</option>)}
                    </select>
                    <input
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        maxLength={500}
                        placeholder="Why? (optional, shown on the timeline)"
                        className="h-8 px-2 text-sm border border-stone-200 rounded-md"
                    />
                    <p className="text-xs text-stone-500">Moves the referral credit only. Original Acquisition and the Contact Owner stay as they are.</p>
                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setEditing(false)} className="h-8 px-3 rounded-md border border-stone-200 text-[13px] hover:bg-stone-50">Cancel</button>
                        <button type="button" disabled={saving} onClick={save} data-testid="attribution-save" className="h-8 px-3 rounded-md bg-stone-900 text-white text-[13px] hover:bg-stone-800 disabled:opacity-50">
                            Save
                        </button>
                    </div>
                </div>
            )}
            {isAdmin && !editing && attribution && (
                <button type="button" onClick={startEdit} data-testid="attribution-correct" className="mt-3 h-7 px-2.5 rounded-md border border-stone-200 text-[12.5px] font-medium inline-flex items-center gap-1.5 hover:bg-stone-50">
                    <Pencil className="w-3 h-3" /> Correct credit
                </button>
            )}
        </div>
    );
}
